import qs from 'qs';

import {
  defaultLocale,
  localeBuildSeparately,
  localeBuildTogether,
} from '@/lib/i18n/dictionaries';
import { Logger } from '@/lib/Logging';

import { EntryVersion } from '@/cms/types/entry';
import { cmsApiEndpoint, isBuildPhase, isProd } from '@/constant/env';

// Build-time request tracking
let buildRequestCounter = 0;
let buildCacheHits = 0;
const buildRequestLog = new Map<
  string,
  { total: number; retries: number; lastAttempt: number }
>();

// Export function to track cache hits from page components
export function trackCacheHit() {
  if (isBuildPhase) {
    buildCacheHits++;
  }
}

export function logBuildRequestStats() {
  if (!isBuildPhase || buildRequestLog.size === 0) return;

  console.log('\n=== BUILD FETCH REQUEST STATISTICS ===');
  console.log(`Total API requests made: ${buildRequestCounter}`);
  console.log(`Cache hits (requests avoided): ${buildCacheHits}`);
  console.log(
    `Cache hit ratio: ${
      buildCacheHits > 0
        ? Math.round(
            (buildCacheHits / (buildRequestCounter + buildCacheHits)) * 100
          )
        : 0
    }%`
  );
  console.log('Breakdown by endpoint:');

  Array.from(buildRequestLog.entries())
    .sort(([, a], [, b]) => b.total - a.total) // Sort by total requests desc
    .forEach(([url, stats]) => {
      const endpoint = url.replace(cmsApiEndpoint || '', '').split('?')[0];
      console.log(
        `  ${endpoint}: ${stats.total} requests, ${stats.retries} retries`
      );
    });

  const totalRetries = Array.from(buildRequestLog.values()).reduce(
    (sum, stats) => sum + stats.retries,
    0
  );
  console.log(`Total retries: ${totalRetries}`);
  console.log('=====================================\n');
}

// Log final stats when build process exits
if (isBuildPhase) {
  process.on('exit', () => {
    logBuildRequestStats();
  });
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
export type ApiResponse<T> = {
  data: T | null;
  error: string | null;
};

export type ApiCallProps = {
  url: string;
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal | null;
  next?: NextFetchRequestConfig;
};

export async function fetchWithRetry(
  url: string,
  options: RequestInit = {},
  retries = 6,
  backoff = 3000,
  isRetry = false
): Promise<Response> {
  // Track requests during build phase
  if (isBuildPhase) {
    buildRequestCounter++;
    const urlKey = url.split('?')[0]; // Remove query params for cleaner grouping
    const existing = buildRequestLog.get(urlKey) || {
      total: 0,
      retries: 0,
      lastAttempt: Date.now(),
    };

    if (isRetry) {
      existing.retries++;
    } else {
      existing.total++;
    }
    existing.lastAttempt = Date.now();
    buildRequestLog.set(urlKey, existing);
  }

  try {
    // Force new connections by disabling keep-alive during build phase
    // This prevents requests from sticking to a single failed pod in Kubernetes
    const fetchOptions = {
      ...options,
      headers: {
        ...options.headers,
        // Disable HTTP keep-alive to ensure load balancer distributes requests
        Connection: 'close',
      },
    };

    const res = await fetch(url, fetchOptions);
    if (!res.ok) {
      throw new Error(`Fetch failed with status ${res.status}`);
    }
    return res;
  } catch (err) {
    if (retries > 0) {
      console.warn(`Retrying ${url} (${retries} left)...`);
      // Add random jitter to backoff to prevent synchronized retries
      // This further helps distribute requests across different pods
      const jitter = Math.random() * 1000; // 0-1 second random delay
      const delay = backoff + jitter;
      await new Promise((r) => setTimeout(r, delay));
      return fetchWithRetry(url, options, retries - 1, backoff * 2, true);
    } else {
      // Log final statistics when build fails
      if (isBuildPhase) {
        logBuildRequestStats();
      }
      throw new Error(`Failed to fetch ${url}: ${err}`);
    }
  }
}

export async function apiCall<T>({
  url,
  method,
  body,
  headers,
  signal,
  next,
}: ApiCallProps): Promise<ApiResponse<T> | undefined> {
  const defaultHeaders = {
    'Content-Type': 'application/json',
    ...headers,
  };

  const options: RequestInit = {
    method,
    headers: body instanceof FormData ? headers : defaultHeaders,
    body: body instanceof FormData ? body : JSON.stringify(body),
    signal,
  };

  try {
    let response;
    if (isBuildPhase) {
      response = await fetchWithRetry(url, {
        ...options,
        next,
      });
    } else {
      response = await fetch(url, {
        ...options,
        next,
      });
    }

    const logger = new Logger('apiCall.ts');

    if (!response.ok) {
      logger.error({
        message: 'Error',
        context: {
          statusText: response.statusText,
          status: response.status,
          url: response.url,
        },
      });
      // Check if response body
      try {
        const errorBody = await response.json();
        return { data: null, error: errorBody };
      } catch (error) {
        return {
          data: null,
          error: response.statusText || `${response.status}`,
        };
      }
    }
    const data = (await response.json()) as T;
    return { data, error: null };
  } catch (error) {
    return { data: null, error: String(error) };
  }
}

export function withStrapiHeaders(
  headers: Record<string, string>
): Record<string, string> {
  const token = process.env.NEXT_PUBLIC_STRAPI_API_TOKEN;

  let headersWithStrapi = {
    Authorization: `Bearer ${token}`,
    'Strapi-Response-Format': 'v4',
  };

  if (headers) {
    headersWithStrapi = { ...headersWithStrapi, ...headers };
  }

  return headersWithStrapi;
}

export async function apiCallStrapi<ResponseT>({
  path,
  query,
  method,
  headers = {},
  body,
  status,
  signal,
  next,
}: {
  path: string;
  query?: Record<string, unknown>;
  method?: HttpMethod;
  headers?: ApiCallProps['headers'];
  body?: ApiCallProps['body'];
  status?: EntryVersion;
  signal?: AbortSignal | null;
  next?: NextFetchRequestConfig;
}) {
  const locale = defaultLocale;

  const { filters, ...queryRest } = query || {};

  // TODO replace with simple query string ourselfs
  const queryString = qs.stringify({
    status: status ?? (isProd ? 'published' : 'draft'),
    filters: {
      locale: {
        $in: localeBuildSeparately.includes(locale)
          ? [locale]
          : localeBuildTogether,
      },
      ...(filters || {}),
    },
    ...queryRest,
  });

  const requestUrl =
    cmsApiEndpoint + path + (queryString ? `?${queryString}` : '');

  const response = await apiCall<ResponseT>({
    url: requestUrl,
    method,
    body,
    headers: withStrapiHeaders(headers),
    signal,
    next,
  });

  return response?.data;
}
